<template>
  <!--
    VendorCodeCellElementLite, multi-root.
    Артикул позиции (ItemVendorCode) и движения (MovementVendorCode).
    Строка не переносится, иконка info это ассет info-circle.svg, клонирование это
    материальная content_copy. Кнопки скрыты и появляются при наведении на ячейку.
    Окна инфо по позиции и по движению открываются через $q.dialog в корне приложения,
    а не внутри ячейки: иначе тяжёлый диалог висел бы в каждой строке таблицы.
  -->
  <span class="qtl-vendor">
    <span class="qtl-vendor__text" v-html="renderedHtml" />

    <button
      v-if="entityId"
      type="button"
      class="qtl-vendor__btn"
      :title="t('info')"
      :aria-label="t('info')"
      @click.stop="openInfo"
    >
      <app-icon
        name="info-circle-outline"
        width="18px"
        color="#979797"
        hover-color="#499259"
        cursor="pointer"
      />
    </button>

    <button
      v-if="isItem"
      ref="cloneBtnRef"
      type="button"
      class="qtl-vendor__btn qtl-vendor__btn--clone"
      :disabled="!storageWA"
      :title="t('clone')"
      :aria-label="t('clone')"
      @click.stop="openCloneConfirm"
    >
      <q-icon name="content_copy" size="18px" />
    </button>
  </span>

  <!-- Подтверждение клонирования позиции - lite-поповер вместо q-menu/q-card. -->
  <mosta-context-menu
    v-if="isItem"
    :visible="cloneConfirmOpen"
    :position="cloneMenuPos"
    @close="cloneConfirmOpen = false"
  >
    <div class="qtl-vendor-confirm">
      <div class="qtl-vendor-confirm__text">{{ t('sure_clone_position') }}</div>
      <div class="qtl-vendor-confirm__actions">
        <button type="button" class="qtl-vendor-confirm__btn" @click="cloneConfirmOpen = false">
          {{ t('cancel') }}
        </button>
        <button type="button" class="qtl-vendor-confirm__btn qtl-vendor-confirm__btn--accent" @click="confirmClone">
          {{ t('clone') }}
        </button>
      </div>
    </div>
  </mosta-context-menu>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useQuasar } from "quasar";
import { i18n } from "@/i18n";
import type { TableDataCell } from "@/models/interfaces/entity-settings/querytable/querytable";
import { ColumnCategory } from "@/models/etc/types";
import { storageStore } from "@/store/storage/storage-instances";
import { storagePositionsStore } from "@/store/storage/storage-positions";
import { formatSimpleHtml } from "./cellFormat";
import MostaContextMenu from "@/components/common/basic/MostaContextMenu.vue";
import ItemInfoDialog from "@/components/multiedit/temporary/storageshared/ItemInfoDialog.vue";
import MoveInfoDialog from "@/components/multiedit/temporary/storageshared/MoveInfoDialog.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";

const props = defineProps<{
  element: TableDataCell;
  entityId?: number;
}>();

const t = (k: string) => i18n.global.t(k);
const $q = useQuasar();
const storage = storageStore();
const storagePos = storagePositionsStore();

const renderedHtml = computed(() => formatSimpleHtml(props.element, t));
const isItem = computed(() => props.element.Category === ColumnCategory.ItemVendorCode);
const storageWA = computed(() => !!storage.storageAccess?.CanWrite);

// Диалог инфо открываем через $q.dialog (монтаж в корне приложения), а не inline
// q-dialog в шаблоне ячейки: ячейка живёт внутри виртуализированной таблицы и при
// пере-рендере строки локальный флаг открытия сбрасывался бы, закрывая окно.
// createType для движения НЕ передаём: тип подгрузит preloadExistingMovement по id.
function openInfo() {
  if (!props.entityId) return;
  if (isItem.value) {
    $q.dialog({
      component: ItemInfoDialog,
      componentProps: { existingPositionId: props.entityId },
    });
  } else {
    $q.dialog({
      component: MoveInfoDialog,
      componentProps: { existingMovementId: props.entityId },
    });
  }
}

const cloneConfirmOpen = ref(false);
const cloneMenuPos = ref<{ top: string; left: string }>({ top: "0px", left: "0px" });
const cloneBtnRef = ref<HTMLElement | null>(null);

function openCloneConfirm(ev: MouseEvent) {
  if (!storageWA.value) return;
  const rect = cloneBtnRef.value?.getBoundingClientRect();
  if (rect) {
    cloneMenuPos.value = { top: `${rect.bottom + 2}px`, left: `${rect.left}px` };
  } else {
    cloneMenuPos.value = { top: `${ev.clientY}px`, left: `${ev.clientX}px` };
  }
  cloneConfirmOpen.value = true;
}

function confirmClone() {
  if (props.entityId) storagePos.cloneStorageItem(props.entityId);
  cloneConfirmOpen.value = false;
}
</script>

<style lang="scss" scoped>
.qtl-vendor {
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 4px;
  white-space: nowrap;
}

.qtl-vendor__text {
  font-size: 13px;
  white-space: nowrap;
}

.qtl-vendor__btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0 2px;
  line-height: 0;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.2s;
}

.qtl-vendor__btn--clone {
  color: #616161;}

.qtl-vendor__btn:disabled {
  color: #cfcfcf;
  cursor: default;
}

.qtl-vendor:hover .qtl-vendor__btn {
  visibility: visible;
  opacity: 1;
}

.qtl-vendor-confirm {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 10px 12px;
  min-width: 220px;
}

.qtl-vendor-confirm__text {
  font-size: 13px;
  margin-bottom: 8px;
}

.qtl-vendor-confirm__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.qtl-vendor-confirm__btn {
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 13px;
  padding: 4px 8px;
  border-radius: 3px;
  color: #333;
}

.qtl-vendor-confirm__btn:hover {
  background: #f2f2f2;
}

.qtl-vendor-confirm__btn--accent {
  color: #3F884F;
  font-weight: 500;
}
</style>
